/**
 * Students list page — EntityTable framework.
 *
 * Server-side sort + page-number pagination with accurate total count.
 * Edit pre-load bug fixed: "edit" row action fetches the full record by ID
 * before opening the form modal (never trusts stale row-cache data).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import { useTenantAwareEntityList } from '@/components/entity/useTenantAwareEntityList';
import { StudentFormModal } from '@features/people/components/StudentFormModal';
import {
  useDeleteStudent,
  getStudent,
  type StudentResponse,
  type ListStudentsPagedParams,
} from '@features/people/api/students';
import { studentsTableConfig } from '@features/people/config/studentsEntityConfig';
import { ImportExportButtons } from '@features/imports';
import type { EntityActionKey } from '@/components/entity/types';

const PAGE_SIZE = 20;

export const Students: React.FC = () => {
  const navigate = useNavigate();

  // ── List state ───────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(studentsTableConfig.defaultSort ?? 'created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>(
    studentsTableConfig.defaultOrder ?? 'desc',
  );
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    studentsTableConfig.defaultFilters ?? {},
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // ── Modal state ──────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editStudent, setEditStudent] = useState<StudentResponse | undefined>();
  const [editLoading, setEditLoading] = useState(false);

  // ── Query params ─────────────────────────────────────────────────────
  const queryParams: ListStudentsPagedParams = {
    status: filters.status !== 'all' ? filters.status : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const {
    data: result,
    isLoading,
    error,
    refetch,
    isAllMode,
    focusRowTenant,
  } = useTenantAwareEntityList<StudentResponse>({
    queryKey: ['students', 'list', queryParams],
    apiPath: studentsTableConfig.apiPath,
    params: queryParams as unknown as Record<string, string | number | boolean | undefined | null>,
  });
  const deleteMutation = useDeleteStudent();

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleSortChange = (newSort: string, newOrder: 'asc' | 'desc') => {
    setSort(newSort);
    setOrder(newOrder);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleRowClick = (student: StudentResponse) => {
    // In "All" mode the row may belong to another school; select its tenant
    // first so the detail page fetches from the right one instead of 404ing
    // against the caller's home tenant.
    focusRowTenant(student);
    navigate(`/students/${student.id}`);
  };

  /** Edit pre-load fix: always fetch fresh record by ID before opening modal. */
  const handleRowAction = async (action: EntityActionKey, student: StudentResponse) => {
    if (action === 'view') {
      focusRowTenant(student);
      navigate(`/students/${student.id}`);
    } else if (action === 'edit') {
      setEditLoading(true);
      try {
        const fresh = await getStudent(student.id);
        setEditStudent(fresh);
        setIsModalOpen(true);
      } catch {
        setEditStudent(student); // fallback to row data if fetch fails
        setIsModalOpen(true);
      } finally {
        setEditLoading(false);
      }
    } else if (action === 'archive') {
      if (!window.confirm(`Archive ${student.first_name} ${student.last_name}?`)) return;
      await deleteMutation.mutateAsync(student.id);
    }
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading ? 'Loading…' : `${total.toLocaleString()} total students`;

  return (
    <PageContainer
      title="Students"
      subtitle={subtitle}
      data-testid="candeza-students-page"
      data-flow="students-page"
      actions={
        <>
          <ImportExportButtons
            entity="students"
            entityLabel="Students"
            selectedIds={selectedIds}
            exportStatus={filters.status !== 'all' ? filters.status : undefined}
            exportSearch={search || undefined}
          />
          <Button
            onClick={() => { setEditStudent(undefined); setIsModalOpen(true); }}
            data-testid="candeza-students-add-btn"
            data-flow="student-new-btn"
            disabled={editLoading || isAllMode}
            title={isAllMode ? 'Select a specific school to add a student' : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Student
          </Button>
        </>
      }
    >
      <Card padding="lg">
        <EntityTable
          config={studentsTableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={handleRowClick}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
          isAllMode={isAllMode}
        />
      </Card>

      <StudentFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditStudent(undefined); }}
        student={editStudent}
      />
    </PageContainer>
  );
};
